/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import { useSelector, connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Container, Box, Typography,
  Divider, Stack, Drawer, Button, Select, MenuItem, FormControl, InputLabel, ToggleButtonGroup,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2'; // Use new GridV2
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Helmet } from 'react-helmet';
import { useInViewport } from 'react-in-viewport';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Toggle from '../common/Toggle';
import AmountCard from './components/AmountCard';
import ReinvestCard from './components/ReinvestCard';
import ReinvestDialog from './components/ReinvestDialog';
import DistributionsDataDisplay from './components/DistributionsDataDisplay';
import {
  getComparator, sortAlphanumerically, sortByDate,
} from '../../../shared/hooks/useSort';
import useUserDistributions from '../common/useUserDistributions';
import TotalDistributionTooltip from './components/TotalDistributionTooltip';
import Search from '../common/Search';
import FundFilter from './components/FundFilter';
import formatValueAsUSD from '../../../shared/helpers/formatValueAsUSD';

// const unPaidDistributionsSubtitle = `* Distributions for funds that total less than $250 will
// be held by AV Until either a) your total distribution for that fund exceeds $250 or
// b) November, which is the last distribution of each calendar year, at which point
// all fund distributions will be processed no matter the size of the check.`;

// eslint-disable-next-line max-len
const unPaidDistributionsSubtitle = 'Distribution payments will remain as "Pending" until the payment is issued. Once issued, the line item will move to the "Paid" tab. All payments, regardless of amount, will be processed by the end of the calendar year.';
const paidDistributionsSubtitle = (
  <>
    Distribution payments will display as "Issued" until the payment is successfully deposited. Once deposited, the status will update to "Deposited." If a check remains uncashed for 90 days or an electronic transfer fails, the payment will revert to "Pending" and move back to the "Unpaid" tab. If this occurs, please contact{' '}
    <a href="mailto:investorrelations@av.vc">investor.relations@av.vc</a> with updated banking or address details.
  </>
);

const customSearchHandler = (data, searchValue) => {
  const caseInsensitiveSearchFilter = searchValue.toLowerCase();
  const filtered = [...data].map((yearDatum) => {
    const yearDatumCopy = { ...yearDatum };
    yearDatumCopy.distribution = [...yearDatumCopy.distribution].filter((datum) => {
      const fundMatch = datum.fundName && datum.fundName.toLowerCase().includes(caseInsensitiveSearchFilter);
      const companyMatch = datum.subDistributions && datum.subDistributions.filter((subDistribution) => (
        subDistribution.companyName && subDistribution.companyName.toLowerCase().includes(caseInsensitiveSearchFilter)
      )).length > 0;
      return fundMatch || companyMatch;
    });
    return yearDatumCopy;
  }).filter((yearDatum) => yearDatum.distribution.length > 0);
  return filtered;
};

const Distribution = () => {
  // TODO: Remove managed user check
  const currentManagedUser = useSelector((state) => state.currentManagedUser);
  const [showUnPaidDistributions, setShowUnPaidDistributions] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [sortBy, setSortBy] = useState('datePaid-desc');
  const [displayedData, setDisplayedData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [unPaidDistributions, setUnPaidDistributions] = useState([]);
  const [reinvestDialogOpen, setReinvestDialogOpen] = useState(false);
  const [yearFilter, setYearFilter] = useState('12months');
  const mobileReinvestCard = useRef();
  const [twelveMonthsTotal, setTwelveMonthsTotal] = useState();

  const {
    userDistributions,
    isLoading,
    isError,
  } = useUserDistributions(currentManagedUser ? `?userid=${currentManagedUser}` : '');

  const reinvestStartDate = '2026-10-21T15:00:00';
  const reinvestEndDate = '2026-10-29T23:59:59';

  const showReinvest = () => {
    const startDate = new Date(reinvestStartDate);
    const endDate = new Date(reinvestEndDate);

    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    return now >= startDate && now <= endDate;
  };

  const { inViewport: reinvestCardInViewport } = useInViewport(mobileReinvestCard);

  const paidDistributionsExist = !!(
    userDistributions.paidDistributions
    && userDistributions.paidDistributions.length
  );
  const unPaidDistributionsExist = !!(
    userDistributions.unPaidDistributions
    && userDistributions.unPaidDistributions.length
  );

  const loadTwelveMonthsDistributions = () => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    let totalDistributions = 0;
    const twelveMonthspaidDistributionData = userDistributions.paidDistributions
      .map((yearDistributions) => {
        // Filter distributions for the current year based on the datePaid field
        const filteredDistributions = yearDistributions.distribution.filter((distribution) => {
          const datePaid = new Date(distribution.datePaid);
          return datePaid >= oneYearAgo && datePaid <= today;
        });

        const totalDistributionAmount = filteredDistributions.reduce((total, distibution) => total + parseFloat(distibution.totalSubdistributions.replace(/[$,]/g, '')), 0);

        // If no filtered distributions, return null (will be removed later)
        if (filteredDistributions.length === 0) {
          return null;
        }

        totalDistributions += totalDistributionAmount;

        return {
          year: yearDistributions.year,
          distribution: filteredDistributions,
          totalAnnualDistribution: formatValueAsUSD(totalDistributionAmount, 2),
        };
      })
      // Remove any null values that result from years with no distributions
      .filter(Boolean);

    setTwelveMonthsTotal(formatValueAsUSD(totalDistributions, 2));
    return twelveMonthspaidDistributionData;
  };

  useEffect(() => {
    if (!userDistributions.paidDistributions) {
      return;
    }
    // pull unique funds and companies from all distributions to create search options
    const funds = new Set();
    const companies = new Set();

    // Iterate through the paidDistributions and extract fundName and companyName
    userDistributions.paidDistributions.forEach((yearDistributions) => {
      yearDistributions.distribution.forEach((distribution) => {
        funds.add(distribution.fundName); // Add fundName to the set
        distribution.subDistributions.forEach((subDistribution) => {
          companies.add(subDistribution.companyName); // Add companyName to the set
        });
      });
    });

    const options = [];

    // Sort funds and companies alphanumerically and push them to the options array
    Array.from(funds).sort(sortAlphanumerically)
      .forEach((fund) => options.push({
        type: 'Fund',
        field: 'fundName',
        label: fund,
      }));

    Array.from(companies).sort(sortAlphanumerically)
      .forEach((company) => options.push({
        type: 'Company',
        field: 'companyName',
        label: company,
      }));

    // Set the search options in state
    setSearchOptions(options);

    const paidDistributionsSorted = userDistributions.paidDistributions.sort(
      getComparator('desc', 'year', sortByDate),
    );
    userDistributions.paidDistributions.forEach((yearDistributions) => {
      if (yearDistributions.distribution && yearDistributions.distribution.length) {
        yearDistributions.distribution.forEach((paidDistribution) => {
          if (paidDistribution.subDistributions && paidDistribution.subDistributions.length) {
            paidDistribution.subDistributions.sort((subA, subB) => {
              if (subA.fundName === subB.fundName) {
                return subA.companyName.localeCompare(subB.companyName);
              }
              return subA.fundName.localeCompare(subB.fundName);
            });
          }
        });
      }
    });

    const twelveMonthspaidDistributionData = loadTwelveMonthsDistributions(paidDistributionsSorted);

    // Check the sort direction and run an initial sort of the newly loaded/changed distributions data
    // Then update the displayed data with this newly sorted data
    if (!yearFilter || yearFilter === 'all') {
      setSortedData(paidDistributionsSorted);
      setDisplayedData(paidDistributionsSorted);
    } else if (yearFilter === '12months') {
      setSortedData(twelveMonthspaidDistributionData);
      setDisplayedData(twelveMonthspaidDistributionData);
    } else {
      const paidDistributionData = userDistributions.paidDistributions.filter((distribution) => (
        distribution.year === yearFilter
      ));
      setSortedData(paidDistributionData);
      setDisplayedData(paidDistributionData);
    }
  }, [userDistributions.paidDistributions, yearFilter]);

  const onSearchClear = () => {
    setDisplayedData(sortedData);
  };

  const onSearch = (filtered) => {
    setDisplayedData(filtered);
  };

  // filter current quarter distributions by fund and set displayed quarter data
  useEffect(() => {
    if (!userDistributions.unPaidDistributions) {
      setUnPaidDistributions([]);
      return;
    }
    const sortedDistributions = [...userDistributions.unPaidDistributions]
      .sort((a, b) => a.fundName.localeCompare(b.fundName));
    setUnPaidDistributions(sortedDistributions);
  }, [userDistributions.unPaidDistributions]);

  const handleYearFilterChange = (event) => {
    setYearFilter(event.target.value);
  };

  return (
    <>
      <Helmet>
        <title>Distributions - Alumni Ventures</title>
        <link rel="shortcut icon" href={`${process.env.AVG_API_PATH}/favicon.ico`} sizes="16x16" />
      </Helmet>
      <Container maxWidth="xl">
        <Box pt={4}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent={{ xs: 'center', sm: 'start' }}
          >
            <Typography variant="h1">Distributions</Typography>
            <Toggle
              value={showUnPaidDistributions}
              setValue={setShowUnPaidDistributions}
              toggleOptions={['Paid Distributions', 'Unpaid Distributions']}
            />
          </Stack>
          <Divider variant="fullWidth" sx={{ my: 2, display: { xs: 'none', sm: 'block' } }} />
          <Grid
            container
            direction={{ xs: 'column', sm: 'column', md: 'row' }}
            spacing={!showUnPaidDistributions || showReinvest() ? 3 : 0}
            justifyContent={{ xs: 'center', sm: 'center', md: 'start' }}
            alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
            pt={{ xs: 2, sm: 1 }}
          >
            <Grid
              container
              direction="column"
              md="auto"
              rowSpacing={{ xs: 1, sm: 1, md: 2 }}
              columnSpacing={0}
            >
              {(!isError && !showUnPaidDistributions) && (
                <Grid>
                  <AmountCard
                    label="TOTAL DISTRIBUTIONS PRIOR TO 2022"
                    amount={userDistributions.distributionTotalBefore2022}
                    paletteColor="lightGreen"
                    isLoading={isLoading}
                    toolTip={<TotalDistributionTooltip />}
                  />
                </Grid>
              )}
              {(!isError && !showUnPaidDistributions) && (
                <Grid>
                  <AmountCard
                    label="TOTAL LIFETIME DISTRIBUTIONS"
                    amount={userDistributions.lifeTimeDistribution}
                    paletteColor="beige"
                    isLoading={isLoading}
                  />
                </Grid>
              )}
              {showUnPaidDistributions && (
                <Grid sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                  {showReinvest() && <ReinvestCard setReinvestDialogOpen={setReinvestDialogOpen} /> }
                </Grid>
              )}
            </Grid>
            <Grid
              xs={12}
              sm={12}
              md
              alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
              pt={{ xs: 3, md: 1 }}
            >
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: showUnPaidDistributions ? 'column' : 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'center', sm: 'center', md: showUnPaidDistributions ? 'start' : 'center' }}
                  textAlign={{ xs: 'center', sm: 'center', md: 'left' }}
                  spacing={2}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 5,
                  }}
                  >
                    <Stack spacing={2}>
                      <Typography variant="h2">
                        { showUnPaidDistributions ? 'Unpaid Distributions' : 'All Paid Distributions' }
                      </Typography>
                      <Typography variant="body2">
                        {!showUnPaidDistributions && paidDistributionsSubtitle }
                      </Typography>
                    </Stack>
                    {(!showUnPaidDistributions && paidDistributionsExist) && (
                    <Search
                      label="Search fund or company"
                      data={sortedData}
                      fieldsToSearchOn={['companyName', 'fundName']}
                      searchOptions={searchOptions}
                      onClear={onSearchClear}
                      onSearch={onSearch}
                      customSearchHandler={customSearchHandler}
                      sx={{ display: { xs: 'none', md: 'block' } }}
                    />
                    )}
                  </Box>
                  <Typography variant="body2">
                    {!!showUnPaidDistributions && unPaidDistributionsSubtitle }
                  </Typography>
                  {(!showUnPaidDistributions && paidDistributionsExist) && (
                  <Stack direction="row" spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
                    <FormControl
                      size="small"
                      fullWidth
                      sx={{ display: { xs: 'flex', md: 'none' } }}
                    >
                      <InputLabel
                        id="filterBy-label"
                        sx={{
                          fontSize: '0.875rem', // Smaller font size for the label
                          zIndex: 1, // Ensure label is above other elements
                        }}
                      >Search by Year
                      </InputLabel>
                      <Select
                        labelId="filterBy-label"
                        id="filterBy"
                        value={yearFilter}
                        onChange={handleYearFilterChange}
                        label="Search by Year"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          minWidth: '150px',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: '2px solid #9A9A964D', // Removes default border
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#7B5599', // Dropdown arrow area color
                            backgroundColor: '#F5F1F9',
                            position: 'absolute',
                            top: '0px',
                            right: '2px',
                            bottom: '0px',
                            display: 'block',
                            height: '35px',
                            width: '32px',
                          },
                          paddingRight: '5px',
                        }}
                      >
                        <MenuItem value="12months">Last 12 Months</MenuItem>
                        {userDistributions.paidDistributions.map((fundDistributions) => (
                          <MenuItem key={fundDistributions.year} value={fundDistributions.year}>
                            {fundDistributions.year}
                          </MenuItem>
                        ))}
                        <MenuItem value="all">All</MenuItem>
                      </Select>
                    </FormControl>
                    {(!showUnPaidDistributions && paidDistributionsExist) && (
                    <Search
                      label="Search fund or company"
                      data={sortedData}
                      fieldsToSearchOn={['companyName', 'fundName']}
                      searchOptions={searchOptions}
                      onClear={onSearchClear}
                      onSearch={onSearch}
                      customSearchHandler={customSearchHandler}
                    />
                    )}
                  </Stack>
                  )}
                </Stack>
                {(!showUnPaidDistributions && paidDistributionsExist) && (
                <>
                  <Divider variant="fullWidth" sx={{ my: 2, display: { xs: 'none', sm: 'block' } }} />
                  <ToggleButtonGroup
                    value={yearFilter}
                    exclusive
                    onChange={(event, year) => {
                      if (year !== null) {
                        setYearFilter(year);
                      }
                    }}
                    aria-label="Filter by Year"
                    sx={{
                      rowGap: 1.8,
                      columnGap: 1.5,
                      flexWrap: 'wrap',
                      display: { xs: 'none', sm: 'none', md: 'flex' },
                    }}
                  >
                    <FundFilter
                      label="Last 12 Months"
                      value="12months"
                      amount={twelveMonthsTotal} // tbd

                      // The built-in selected functionality of MUI's ToggleButtonGroup appears to be
                      // bugged in some manner with this custom component,
                      // manually setting the selected property is an easy solution
                      selected={yearFilter === '12months'}
                    />
                    {userDistributions.paidDistributions.map((fundDistributions) => (
                      <FundFilter
                        key={fundDistributions.year}
                        label={fundDistributions.year}
                        value={fundDistributions.year}
                        amount={fundDistributions.totalAnnualDistribution}
                        fundCode={null}
                        // The built-in selected functionality of MUI's ToggleButtonGroup appears to be
                        // bugged in some manner with this custom component,
                        // manually setting the selected property is an easy solution
                        selected={yearFilter === fundDistributions.year}
                      />
                    ))}
                    <FundFilter
                      label="Jan '22 to Present"
                      value="all"
                      amount={userDistributions.paidTotalDistributions}
                      // The built-in selected functionality of MUI's ToggleButtonGroup appears to be
                      // bugged in some manner with this custom component,
                      // manually setting the selected property is an easy solution
                      selected={yearFilter === 'all'}
                    />
                  </ToggleButtonGroup>
                </>
                )}
                {/* { (!showUnPaidDistributions && paidDistributionsExist) && (
                  <Box sx={{ alignSelf: 'flex-end', minWidth: 145 }}>
                    <FormControl size="small" fullWidth sx={{ mt: { xs: 1, sm: 1, md: 0 } }}>
                      <InputLabel id="sortBy-label">Sort by</InputLabel>
                      <Select
                        labelId="sortBy-label"
                        id="sortBy"
                        value={sortBy}
                        onChange={handleSortByChange}
                        label="Sort by"
                      >
                        <MenuItem value="datePaid-desc" comparator="date">Date: Newest</MenuItem>
                        <MenuItem value="datePaid-asc" comparator="date">Date: Oldest</MenuItem>
                        <MenuItem value="fundName-asc">Fund: A - Z</MenuItem>
                        <MenuItem value="fundName-desc">Fund: Z - A</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )} */}
                {isLoading ? (
                  <Box sx={{ '&&': { my: 5, mx: 'auto' } }}>
                    <Loader />
                  </Box>
                ) : (
                  <DistributionsDataDisplay
                    isError={isError}
                    showUnPaidDistributions={showUnPaidDistributions}
                    unPaidDistributionsExist={unPaidDistributionsExist}
                    paidDistributionsExist={paidDistributionsExist}
                    displayedData={displayedData}
                    unPaidDistributions={unPaidDistributions}
                  />
                )}
                { showReinvest() && (
                <ReinvestCard
                  ref={mobileReinvestCard}
                  setReinvestDialogOpen={setReinvestDialogOpen}
                  size="wide"
                  sx={{
                    alignSelf: 'center',
                    // can't conditionally render component based on showUnPaidDistributions
                    // because it will break the in viewport ref functionality, so we have to
                    // conditionally change the display css property instead
                    display: {
                      xs: showUnPaidDistributions ? 'block' : 'none',
                      sm: showUnPaidDistributions ? 'block' : 'none',
                      md: 'none',
                    },
                  }}
                />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
        { showReinvest() && (
        <Drawer
          anchor="bottom"
          variant="persistent"
          open={!reinvestCardInViewport && showUnPaidDistributions}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}
        >
          <Box p={2} m="auto">
            <Button
              type="button"
              variant="contained"
              endIcon={<NavigateNextIcon />}
              onClick={() => setReinvestDialogOpen(true)}
            >
              I want to re-invest
            </Button>
          </Box>
        </Drawer>
        )}
        <ReinvestDialog
          open={reinvestDialogOpen}
          handleClose={() => setReinvestDialogOpen(false)}
        />
      </Container>
    </>
  );
};

Distribution.propTypes = {};

const mapStateToProps = (state) => ({
  dispatch: state.dispatch,
});

export default withRouter((connect(mapStateToProps)(Distribution)));
